// src/pages/seasons/RegisterPage.jsx
// Public registration form — no login required  (v2 redesign)
'use client';

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  doc, getDoc, setDoc, updateDoc, collection, addDoc, deleteDoc,
  serverTimestamp, query, where, getDocs
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { format } from 'date-fns';
import { es as dateLocaleEs } from 'date-fns/locale';
import { es } from 'date-fns/locale';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { setAppLanguage } from '@/i18n';

import { Input } from '@/components/input';
import { Textarea } from '@/components/textarea';
import { Button } from '@/components/button';
import { Field, Label as CatalystLabel, ErrorMessage, Description } from '@/components/fieldset';
import { calculatePrice, fmtMoney } from '@/utils/pricing';
import { getActivePricingTier, formatTierPrice } from '@/utils/pricingTiers';
import { toSeasonDate, endOfSeasonDay } from '@/utils/seasonDates';
import { toE164US, formatUsPhone } from '@/utils/phone';

import {
  CheckCircleIcon,
  CheckIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  SparklesIcon,
  CreditCardIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';

/* ─────────────────────────────────────────────────────────── */
/* Design tokens (lime/pitch system — keep in sync with the    */
/* rest of the app: ClubDashboard, EventDetailPage, etc.)      */
/*                                                             */
/* Why these matter: this page is the FIRST surface a parent   */
/* sees when they receive a registration link. The lime/pitch  */
/* identity replaces the older slate/blue scheme; coordination */
/* with the in-app pages keeps the brand consistent across the */
/* parent's first-touch and ongoing experience.                */
/* ─────────────────────────────────────────────────────────── */
const HEAD_FONT = "'Barlow Condensed', sans-serif";

/* ─────────────────────────────────────────────────────────── */
/* Helpers                                                     */
/* ─────────────────────────────────────────────────────────── */
// Season dates are date-only values — parse via the shared convention
// (local midnight, UTC-midnight legacy repair) so open/close boundaries
// land on the calendar day the coach picked.
const toDate = toSeasonDate;

const RELATIONSHIPS    = ['Mother', 'Father', 'Guardian', 'Grandparent', 'Other'];
const RELATIONSHIPS_ES = ['Madre',  'Padre',  'Tutor/a',  'Abuelo/a',    'Otro'];

/* ─────────────────────────────────────────────────────────── */
/* Bilingual strings                                           */
/* ─────────────────────────────────────────────────────────── */
const T = {
  en: {
    step1: 'Player', step2: 'Parent', step3: 'Payment', step4: 'Waiver',
    playerInfo: 'Player Information',
    firstName: 'First Name', lastName: 'Last Name', dob: 'Date of Birth',
    division: 'Division / Age Group',
    discounts: 'Available Discounts',
    siblingCheck: 'I have another child registered this season',
    returningCheck: 'My child played with this team last season',
    saves: (n) => `Saves ${fmtMoney(n)}`,
    parentTitle: 'Parent / Guardian',
    emergencyTitle: 'Emergency Contact',
    fullName: 'Full Name', email: 'Email', phone: 'Phone', relationship: 'Relationship',
    medicalTitle: 'Medical Notes',
    medicalSub: 'Optional — allergies, conditions, or anything your coach should know.',
    medicalPlaceholder: 'e.g. Asthma — carries inhaler. Allergic to bee stings.',
    paymentTitle: 'How would you like to pay?',
    payFull: 'Pay in Full', payFullSub: 'One payment, done.',
    installments: (n) => `${n} Installments`,
    installmentsSub: (first, rest, n) =>
      `${fmtMoney(first)} today, then ${n - 1}× ${fmtMoney(rest)}`,
    mostPopular: 'Most Popular',
    today: 'Today',
    paySchedule: 'Full payment schedule',
    hideSchedule: 'Hide schedule',
    paymentDue: (n) => `Payment ${n}`,
    waiverTitle: 'Liability Waiver',
    waiverCheck: 'I have read and agree to the liability waiver above on behalf of my child.',
    waiverSign: 'Type your full name to sign',
    waiverSignSub: 'This serves as your electronic signature.',
    waiverSignPlaceholder: 'Your full name',
    esignDisclosure:
      'By typing your full name and submitting this registration, you agree that your typed name constitutes your legal electronic signature under the U.S. ESIGN Act and UETA, and that you intend to sign each waiver listed above on behalf of your child. A record of your signature, including the date, your device, and the exact waiver text, will be stored for both parties.',
    // Paid label signals the Stripe handoff explicitly — without this,
    // some parents click "Complete Registration" expecting to be done
    // and instead get unexpectedly redirected to a Stripe Checkout page,
    // which feels like "wait, did the form even submit?". "Continue to
    // secure payment" + lock icon (rendered alongside in the button)
    // sets the right expectation. Free flows stay direct.
    submitBtn: (amt) => amt > 0 ? `Continue to secure payment — ${fmtMoney(amt)}` : 'Complete Registration — Free',
    submitting: 'Submitting…',
    back: 'Back', next: 'Next',
    dueToday: 'Due today',
    firstOf: (n) => `First of ${n} payments`,
    savingWith: (n) => `You're saving ${fmtMoney(n)} with your discounts`,
    secureNote: (team) => `Your information is stored securely and only shared with ${team}.`,
    basePrice: 'Base price',
    earlyBirdDiscount: 'Early bird discount',
    siblingDiscount: 'Sibling discount',
    returningDiscount: 'Returning player discount',
    subtotal: 'Subtotal',
    processingFee: 'Processing fee',
    processingFeeHint: 'Covers online payment processing (2.9% + $0.30).',
    total: 'Total',
    earlyBird: (n) => `Early Bird — save ${fmtMoney(n)}!`,
    closes: 'Closes',
    spotsLeft: (n) => `Only ${n} spot${n !== 1 ? 's' : ''} left!`,
    spotsRemaining: (n) => `${n} spots remaining`,
    notFoundTitle: 'Registration Not Found',
    notFoundBody: 'This registration link is invalid or has expired. Contact your coach for a new link.',
    closedTitle: 'Registration Closed',
    closedBody: (seasonName, teamName) => `Registration for ${seasonName} is not currently open. Contact ${teamName} for more information.`,
    regEyebrow: 'Registration',
    freeBadge: 'Free',
    errRequired: 'Required.',
    errEmail: 'Valid email required.',
    errDivision: 'Please select a division.',
    errWaivers: 'You must agree to all waivers.',
    errSignName: 'Please type your full name to sign.',
    errFillAll: 'Please fill in all required fields.',
    redirectingPayment: 'Redirecting to payment…',
    fullTitle: 'Season Full',
    fullBody: (seasonName, teamName) => `${seasonName} has reached its maximum number of players. Contact ${teamName} to join the waitlist.`,
    selectDiv: 'Select your division…',
    totalLabel: 'total today',
    firstPayLabel: 'first payment today',
    siblingModalTitle: 'Sibling discount couldn\'t be verified',
    siblingModalBody: 'We couldn\'t find another registration for this season under your email address.',
    siblingModalHint: 'To get the sibling discount, register your other child for this season first — then come back to complete this registration.',
    siblingModalRemove: (amt) => `Remove discount — pay ${fmtMoney(amt)} today`,
    siblingModalRegisterFirst: 'I\'ll register the other child first',
    welcomeBack: 'Welcome back! We pre-filled your name and phone. You can edit them if anything\'s changed.',
    smsOptInLabel: 'Text me urgent updates (weather cancellations, payment issues)',
    smsOptInDisclosure:
      'By checking this box, you agree to receive automated text messages from Scrimly about this registration — including weather cancellations, schedule changes, and payment reminders — at the phone number above. Message & data rates may apply. Reply STOP to opt out, HELP for help. Consent is not a condition of registration.',
    smsPhoneInvalid: 'Please enter a valid US phone number to receive texts.',
    saveForLater: 'Save & finish later',
    saveForLaterHelp: 'We\'ll email you a link so you can pick up where you left off.',
    saveForLaterSending: 'Sending…',
    saveForLaterSent: 'Link sent! Check your email for the resume link.',
    saveForLaterNoEmail: 'Please enter your email first so we know where to send the link.',
    saveForLaterRateLimited: 'A link was just sent. Please check your email (and spam folder) before requesting another.',
    saveForLaterError: 'Could not send the email right now. Please try again in a moment.',
    resumeBanner: 'Welcome back — we restored your registration exactly where you left off.',
    resumeLoadingTitle: 'Restoring your registration…',
  },
  es: {
    step1: 'Jugador', step2: 'Padre', step3: 'Pago', step4: 'Exención',
    playerInfo: 'Información del Jugador',
    firstName: 'Nombre', lastName: 'Apellido', dob: 'Fecha de Nacimiento',
    division: 'División / Grupo de Edad',
    discounts: 'Descuentos Disponibles',
    siblingCheck: 'Tengo otro hijo/a registrado esta temporada',
    returningCheck: 'Mi hijo/a jugó con este equipo la temporada pasada',
    saves: (n) => `Ahorra ${fmtMoney(n)}`,
    parentTitle: 'Padre / Tutor',
    emergencyTitle: 'Contacto de Emergencia',
    fullName: 'Nombre Completo', email: 'Correo Electrónico', phone: 'Teléfono', relationship: 'Parentesco',
    medicalTitle: 'Notas Médicas',
    medicalSub: 'Opcional — alergias, condiciones u otra información importante.',
    medicalPlaceholder: 'Ej. Asma — lleva inhalador. Alérgico a picaduras de abejas.',
    paymentTitle: '¿Cómo deseas pagar?',
    payFull: 'Pago Completo', payFullSub: 'Un solo pago, listo.',
    installments: (n) => `${n} Pagos`,
    installmentsSub: (first, rest, n) =>
      `${fmtMoney(first)} hoy, luego ${n - 1}× ${fmtMoney(rest)}`,
    mostPopular: 'Más Popular',
    today: 'Hoy',
    paySchedule: 'Calendario de pagos',
    hideSchedule: 'Ocultar calendario',
    paymentDue: (n) => `Pago ${n}`,
    waiverTitle: 'Exención de Responsabilidad',
    waiverCheck: 'He leído y acepto la exención de responsabilidad en nombre de mi hijo/a.',
    waiverSign: 'Escriba su nombre completo para firmar',
    waiverSignSub: 'Esto sirve como su firma electrónica.',
    waiverSignPlaceholder: 'Su nombre completo',
    esignDisclosure:
      'Al escribir su nombre completo y enviar este registro, usted acepta que su nombre escrito constituye su firma electrónica legal conforme a las leyes ESIGN y UETA de EE. UU., y que tiene la intención de firmar cada exención en nombre de su hijo/a. Se conservará un registro de su firma, incluyendo la fecha, su dispositivo y el texto exacto de la exención, para ambas partes.',
    submitBtn: (amt) => amt > 0 ? `Continuar al pago seguro — ${fmtMoney(amt)}` : 'Completar Registro — Gratis',
    submitting: 'Enviando…',
    back: 'Atrás', next: 'Siguiente',
    dueToday: 'Pago hoy',
    firstOf: (n) => `Primero de ${n} pagos`,
    savingWith: (n) => `Estás ahorrando ${fmtMoney(n)} con tus descuentos`,
    secureNote: (team) => `Su información se almacena de forma segura y solo se comparte con ${team}.`,
    basePrice: 'Precio base',
    earlyBirdDiscount: 'Descuento inscripción temprana',
    siblingDiscount: 'Descuento hermano/a',
    returningDiscount: 'Descuento jugador retornante',
    subtotal: 'Subtotal',
    processingFee: 'Cargo por procesamiento',
    processingFeeHint: 'Cubre el procesamiento del pago en línea (2.9% + $0.30).',
    total: 'Total',
    earlyBird: (n) => `Inscripción Temprana — ¡ahorra ${fmtMoney(n)}!`,
    closes: 'Cierra',
    spotsLeft: (n) => `¡Solo quedan ${n} lugar${n !== 1 ? 'es' : ''}!`,
    spotsRemaining: (n) => `${n} lugares restantes`,
    notFoundTitle: 'Registro No Encontrado',
    notFoundBody: 'Este enlace de registro no es válido o ha expirado. Contacta a tu entrenador para un nuevo enlace.',
    closedTitle: 'Registro Cerrado',
    closedBody: (seasonName, teamName) => `La inscripción para ${seasonName} no está abierta en este momento. Contacta a ${teamName} para más información.`,
    regEyebrow: 'Registro',
    freeBadge: 'Gratis',
    errRequired: 'Obligatorio.',
    errEmail: 'Se requiere un correo válido.',
    errDivision: 'Selecciona una división.',
    errWaivers: 'Debes aceptar todas las exenciones.',
    errSignName: 'Escribe tu nombre completo para firmar.',
    errFillAll: 'Completa todos los campos obligatorios.',
    redirectingPayment: 'Redirigiendo al pago…',
    fullTitle: 'Temporada Completa',
    fullBody: (seasonName, teamName) => `${seasonName} alcanzó su número máximo de jugadores. Contacta a ${teamName} para unirte a la lista de espera.`,
    selectDiv: 'Selecciona tu división…',
    totalLabel: 'total hoy',
    firstPayLabel: 'primer pago hoy',
    siblingModalTitle: 'No se pudo verificar el descuento por hermano/a',
    siblingModalBody: 'No encontramos otro registro para esta temporada con tu correo electrónico.',
    siblingModalHint: 'Para obtener el descuento, primero registra a tu otro hijo/a en esta temporada y regresa para completar este registro.',
    siblingModalRemove: (amt) => `Quitar descuento — pagar ${fmtMoney(amt)} hoy`,
    siblingModalRegisterFirst: 'Registraré primero al otro hijo/a',
    welcomeBack: '¡Hola de nuevo! Rellenamos tu nombre y teléfono. Puedes editarlos si algo cambió.',
    smsOptInLabel: 'Enviarme mensajes de texto urgentes (cancelaciones por clima, problemas de pago)',
    smsOptInDisclosure:
      'Al marcar esta casilla, aceptas recibir mensajes de texto automáticos de Scrimly sobre esta inscripción, incluyendo cancelaciones por clima, cambios de horario y recordatorios de pago, al número ingresado arriba. Pueden aplicarse tarifas de mensajes y datos. Responde STOP para cancelar, HELP para ayuda. El consentimiento no es requisito para la inscripción.',
    smsPhoneInvalid: 'Por favor ingresa un número de teléfono de EE. UU. válido para recibir mensajes.',
    saveForLater: 'Guardar y continuar después',
    saveForLaterHelp: 'Te enviaremos un enlace por correo para continuar donde lo dejaste.',
    saveForLaterSending: 'Enviando…',
    saveForLaterSent: '¡Enlace enviado! Revisa tu correo para continuar.',
    saveForLaterNoEmail: 'Ingresa tu correo primero para saber a dónde enviar el enlace.',
    saveForLaterRateLimited: 'Ya enviamos un enlace. Revisa tu correo (y la carpeta de spam) antes de pedir otro.',
    saveForLaterError: 'No pudimos enviar el correo. Intenta de nuevo en un momento.',
    resumeBanner: '¡Hola de nuevo! Restauramos tu registro exactamente donde lo dejaste.',
    resumeLoadingTitle: 'Restaurando tu registro…',
  },
};

/* ─────────────────────────────────────────────────────────── */
/* Main component                                              */
/* ─────────────────────────────────────────────────────────── */
export default function RegisterPage() {
  const { teamId, seasonId } = useParams();
  const [searchParams] = useSearchParams();
  const resumeId = searchParams.get('resume') || '';
  const { i18n } = useTranslation();
  const lang = String(i18n.language || 'en').toLowerCase().startsWith('es') ? 'es' : 'en';
  const setLang = (next) => setAppLanguage(typeof next === 'function' ? next(lang) : next);
  const t = T[lang];

  const [season,    setSeason]    = useState(null);
  const [team,      setTeam]      = useState(null);
  const [status,    setStatus]    = useState('loading');
  const [spotsLeft, setSpotsLeft] = useState(null);
  // Per-tier fill counts for tiered pricing (only populated when
  // season.pricingMode === 'tiered'). Drives capacity-aware tier selection
  // and the "X spots left at this price" urgency cue. Recomputed on the
  // initial season load below — no live subscription, since stale-by-30s
  // is acceptable and the server re-resolves the tier at checkout time.
  const [tierFillCounts, setTierFillCounts] = useState({});

  /* ── Draft / resume state ───────────────────────────────────────────────
   * `draftId` is the Firestore doc ID in `registrationDrafts`. Created
   * lazily on the first advancement that includes a valid parentEmail so
   * abandoned-cart recovery can actually reach the parent. On successful
   * submit we mark the draft `promotedToRegistrationId`, which stops the
   * reminder cron from emailing again.
   */
  const [draftId,        setDraftId]        = useState('');
  const [hydrating,      setHydrating]      = useState(!!resumeId);
  const [showResumeBanner, setShowResumeBanner] = useState(false);
  const [saveForLaterState, setSaveForLaterState] = useState('idle'); // idle | sending | sent
  // Avoid redundant writes — stringify the last-saved draft payload and
  // compare before writing. Saves tokens, saves write quota.
  const lastDraftSnapshotRef = useRef('');

  // Internal step always 1-4. Step 3 (Payment) may be auto-skipped.
  const [step,          setStep]         = useState(1);
  const [stepAttempted, setStepAttempted] = useState({ 1: false, 2: false, 3: false, 4: false });
  const [errors,        setErrors]       = useState({});
  const [saving,        setSaving]       = useState(false);
  const [showSiblingModal, setShowSiblingModal] = useState(false);

  const [form, setForm] = useState({
    playerFirstName: '', playerLastName: '', playerDOB: '', division: '',
    hasSibling: false, isReturning: false, paymentPlan: null,
    parentName: '', parentEmail: '', parentPhone: '',
    // TCPA opt-in. Defaulted FALSE — a prechecked box is invalid consent under
    // TCPA/FCC guidance, regardless of how obvious the disclosure looks.
    // We flip this true only if the parent actively checks it OR we pre-fill
    // from a returning-parent profile where they previously consented.
    smsOptIn: false,
    emergencyName: '', emergencyPhone: '', emergencyRelationship: '',
    medicalNotes: '',
    waiverAgreements: {}, // { [waiverIndex]: boolean }
    waiverSignedName: '',
  });

  // Remember-me banner. Set to true briefly after a successful profile
  // lookup pre-fills the parent name/phone. Cleared once the parent types.
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);
  // Don't hammer the backend if the parent tabs in and out of the field.
  const [lastLookedUpEmail, setLastLookedUpEmail] = useState('');

  /* ── Load season + team ─────────────────────────────── */
  useEffect(() => {
    async function load() {
      try {
        const [seasonSnap, teamSnap] = await Promise.all([
          getDoc(doc(db, 'seasons', seasonId)),
          getDoc(doc(db, 'teams',   teamId)),
        ]);
        if (!seasonSnap.exists() || !teamSnap.exists()) { setStatus('error'); return; }

        const s = { id: seasonSnap.id, ...seasonSnap.data() };
        const t = { id: teamSnap.id,   ...teamSnap.data() };
        setSeason(s); setTeam(t);

        const now   = new Date();
        // Merge of two same-day fixes (2026-07-22):
        // - Status is authoritative for OPENING: "Open registration
        //   immediately" sets status 'open' and the season page's OPEN badge
        //   reads status alone, but this gate also required now >=
        //   registrationOpen — so a coach whose form defaulted to a future
        //   open date shared a link that told every parent CLOSED while their
        //   own screen said OPEN (funnel-walk finding #8). Opening is
        //   status-only; Close Reg. flips status and closes instantly.
        // - The CLOSE date still auto-closes an expired window, and is
        //   inclusive of its full day via endOfSeasonDay (the UTC-midnight
        //   fix), so registration no longer closes hours early.
        const close = endOfSeasonDay(s.registrationClose);
        if (s.status !== 'open' || (close && now > close)) { setStatus('closed'); return; }

        // For tiered pricing we need per-tier counts; pull regs once here so
        // we can both (a) honor maxPlayers and (b) fill `tierFillCounts`.
        // For non-tiered we still only need the aggregate count for
        // maxPlayers — keep the original short-circuit when the season has
        // no maxPlayers cap and no tier list.
        //
        // INNER TRY/CATCH (added 2026-05): the /registrations rule requires
        // isAuthenticated(), but parents loading this page are unauthenticated
        // by design. Pre-fix, this query threw permission-denied which fell
        // into the OUTER catch and triggered setStatus('error') → the parent
        // saw "REGISTRATION NOT FOUND" on every season with a maxPlayers cap.
        // Now we treat a failed cap-check query as "show the form anyway" —
        // the createRegistrationCheckout cloud function enforces the cap
        // server-side using Admin SDK at submission time, so over-fill is
        // still impossible.
        const isTieredSeason = s.pricingMode === 'tiered' && Array.isArray(s.pricingTiers) && s.pricingTiers.length > 0;
        if (s.maxPlayers || isTieredSeason) {
          try {
            const countSnap = await getDocs(
              query(collection(db, 'registrations'),
                where('seasonId', '==', seasonId),
                where('status', 'in', ['pending', 'paid', 'confirmed', 'pending_payment'])
              )
            );
            const taken = countSnap.size;
            if (s.maxPlayers && taken >= s.maxPlayers) { setStatus('full'); return; }
            if (s.maxPlayers) setSpotsLeft(s.maxPlayers - taken);
            if (isTieredSeason) {
              const counts = {};
              countSnap.forEach((d) => {
                const tid = d.data().pricingTierId;
                if (tid) counts[tid] = (counts[tid] || 0) + 1;
              });
              setTierFillCounts(counts);
            }
          } catch (capErr) {
            // permission-denied is expected for unauthenticated parents.
            // Log for visibility but DO NOT block the form. Server-side
            // enforces the cap at submission via Admin SDK.
            console.warn('[RegisterPage] cap check skipped (likely unauth parent):', capErr?.code || capErr?.message);
          }
        }
        setStatus('open');
      } catch (err) {
        console.error('[RegisterPage]', err);
        setStatus('error');
      }
    }
    if (teamId && seasonId) load();
  }, [teamId, seasonId]);

  /* ── Hydrate from ?resume=<draftId> ─────────────────────
   * Fetch the draft doc, refill the form, and jump to the step the parent
   * was last on. If the draft is missing, already promoted, or doesn't
   * match this season, we silently fall back to a fresh form (the parent
   * gets no worse experience than if they'd followed a stale link).
   */
  useEffect(() => {
    if (!resumeId) return;
    let cancelled = false;
    async function hydrate() {
      try {
        const snap = await getDoc(doc(db, 'registrationDrafts', resumeId));
        if (cancelled) return;
        if (!snap.exists()) { setHydrating(false); return; }
        const d = snap.data();
        // Stale or already completed — discard.
        if (d.promotedToRegistrationId) { setHydrating(false); return; }
        // Defensive: if the draft was started for a different team/season,
        // don't leak its state into this page. Treat as fresh.
        if ((d.teamId && d.teamId !== teamId) || (d.seasonId && d.seasonId !== seasonId)) {
          setHydrating(false);
          return;
        }
        setForm(prev => ({
          ...prev,
          playerFirstName:        d.playerFirstName        || prev.playerFirstName,
          playerLastName:         d.playerLastName         || prev.playerLastName,
          playerDOB:              d.playerDOB              || prev.playerDOB,
          division:               d.division               || prev.division,
          hasSibling:             !!d.hasSibling,
          isReturning:            !!d.isReturning,
          paymentPlan:            d.paymentPlan ?? null,
          parentName:             d.parentName             || prev.parentName,
          parentEmail:            d.parentEmail            || prev.parentEmail,
          parentPhone:            d.parentPhone            || prev.parentPhone,
          // Restore opt-in state exactly as the parent left it — never
          // re-default to false on resume, that would erase active consent.
          smsOptIn:               typeof d.smsOptIn === 'boolean' ? d.smsOptIn : prev.smsOptIn,
          emergencyName:          d.emergencyName          || prev.emergencyName,
          emergencyPhone:         d.emergencyPhone         || prev.emergencyPhone,
          emergencyRelationship:  d.emergencyRelationship  || prev.emergencyRelationship,
          medicalNotes:           d.medicalNotes           || prev.medicalNotes,
          waiverAgreements:       d.waiverAgreements       || prev.waiverAgreements,
          waiverSignedName:       d.waiverSignedName       || prev.waiverSignedName,
        }));
        setDraftId(resumeId);
        // Jump to the parent's last step. Clamp to [1, 4]. We never
        // auto-resume to step 4 — always send them back one step so they
        // re-check before signing.
        const last = Math.max(1, Math.min(4, Number(d.lastStep) || 1));
        setStep(last >= 4 ? 3 : last);
        setShowResumeBanner(true);
      } catch (err) {
        console.warn('[RegisterPage] resume hydration failed', err?.message);
      } finally {
        if (!cancelled) setHydrating(false);
      }
    }
    hydrate();
    return () => { cancelled = true; };
  }, [resumeId, teamId, seasonId]);

  /* ── Draft save helper ─────────────────────────────────
   * Writes the current form state to `registrationDrafts/{draftId}`. Lazy-
   * creates the doc on first call. Skips the write if nothing changed since
   * the previous save. Silent on failure — the parent can still complete
   * their registration without a draft, it just means no resume support.
   *
   * Returns the draftId (useful for the Save & finish later button flow).
   */
  const saveDraft = useCallback(async (overrides = {}) => {
    // Can't email a resume link without an address.
    const email = String(form.parentEmail || '').trim().toLowerCase();
    if (!email || !/\S+@\S+\.\S+/.test(email)) return null;

    const payload = {
      teamId, seasonId,
      teamName:   team?.name   || null,
      seasonName: season?.name || null,
      playerFirstName:       form.playerFirstName || '',
      playerLastName:        form.playerLastName  || '',
      playerDOB:             form.playerDOB       || '',
      division:              form.division        || '',
      hasSibling:            !!form.hasSibling,
      isReturning:           !!form.isReturning,
      paymentPlan:           form.paymentPlan ?? null,
      parentName:            form.parentName      || '',
      parentEmail:           email,
      parentPhone:           form.parentPhone     || '',
      // Persist consent on the draft so resuming doesn't reset it.
      smsOptIn:              !!form.smsOptIn,
      // Preferred message language — lets the resume/reminder emails
      // route bilingually (same snapshot the final registration takes).
      language:              lang,
      emergencyName:         form.emergencyName   || '',
      emergencyPhone:        form.emergencyPhone  || '',
      emergencyRelationship: form.emergencyRelationship || '',
      medicalNotes:          form.medicalNotes    || '',
      waiverAgreements:      form.waiverAgreements || {},
      waiverSignedName:      form.waiverSignedName || '',
      lastStep:              step,
      updatedAt:             serverTimestamp(),
      ...overrides,
    };

    // Skip redundant writes (common on step back-and-forth without edits).
    const snapshot = JSON.stringify({ ...payload, updatedAt: null });
    if (snapshot === lastDraftSnapshotRef.current) return draftId || null;
    lastDraftSnapshotRef.current = snapshot;

    try {
      if (draftId) {
        await updateDoc(doc(db, 'registrationDrafts', draftId), payload);
        return draftId;
      }
      // Create via addDoc (Firestore-generated 20-char ID = ~120 bits of
      // entropy, safe to use as the access token).
      const newRef = await addDoc(collection(db, 'registrationDrafts'), {
        ...payload,
        createdAt: serverTimestamp(),
      });
      setDraftId(newRef.id);
      return newRef.id;
    } catch (err) {
      console.warn('[RegisterPage] draft save failed', err?.message);
      return draftId || null;
    }
  }, [draftId, form, season, step, team, teamId, seasonId, lang]);
  // Tiered pricing — resolved once on render based on the season tier list +
  // local fill counts. Server re-resolves at checkout creation time (the cap
  // could have filled in the seconds between page-load and submit).
  const isTiered = season?.pricingMode === 'tiered'
    && Array.isArray(season.pricingTiers)
    && season.pricingTiers.length > 0;
  const activeTier = useMemo(() => {
    if (!isTiered) return null;
    return getActivePricingTier(season.pricingTiers, {
      now: new Date(),
      tierFillCounts,
    });
  }, [isTiered, season, tierFillCounts]);

  // For tiered seasons we override the season's flat fee with the active
  // tier's price before delegating to calculatePrice, so all the existing
  // sibling/returning/processing-fee/installment math still applies on top
  // of the tier-resolved base. When no tier is active we fall through with
  // a 0 base (the empty-state UI will block submission anyway).
  const pricing = useMemo(() => {
    if (!season) return null;
    if (isTiered) {
      const tierBase = activeTier?.priceInCents ?? 0;
      return calculatePrice(
        { ...season, registrationFee: tierBase, divisionPricing: null },
        {
          division:    null,
          hasSibling:  form.hasSibling,
          isReturning: form.isReturning,
          paymentPlan: form.paymentPlan,
        }
      );
    }
    return calculatePrice(season, {
      division:    form.division    || null,
      hasSibling:  form.hasSibling,
      isReturning: form.isReturning,
      paymentPlan: form.paymentPlan,
    });
  }, [season, isTiered, activeTier, form.division, form.hasSibling, form.isReturning, form.paymentPlan]);

  const divisions = season?.divisionPricing ? Object.keys(season.divisionPricing) : [];

  // Derive installment options.
  // New format: coach picks a single fixed count in `paymentPlanInstallments`.
  // Legacy format: `paymentPlanOptions` array (e.g. [2, 3]) from pre-Level-2 seasons.
  const installmentOptions = useMemo(() => {
    if (!season?.paymentPlanEnabled) return [];
    const n = Number(season.paymentPlanInstallments);
    if (Number.isFinite(n) && n >= 2) return [n];
    return (season.paymentPlanOptions ?? []).filter(x => Number(x) >= 2);
  }, [season]);

  const hasPaymentPlan = !!(
    season?.paymentPlanEnabled &&
    pricing &&
    pricing.finalAmount > 0 &&
    installmentOptions.length > 0
  );

  /* ── Form handlers ──────────────────────────────────── */
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setForm(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]: null }));
    // As soon as the parent starts editing name/phone themselves, drop the
    // "Welcome back" banner — keeps it from getting stale.
    if (showWelcomeBack && (name === 'parentName' || name === 'parentPhone')) {
      setShowWelcomeBack(false);
    }
  }, [errors, showWelcomeBack]);

  /* ── Remember-me pre-fill ───────────────────────────────
   * On email blur, ask the backend whether this parent registered
   * previously. If yes, pre-fill empty name + phone fields and show a
   * "Welcome back" banner. We never overwrite values the parent has
   * already typed. Fully non-blocking — lookup failure just means no
   * pre-fill, which is the default experience anyway.
   */
  const handleParentEmailBlur = useCallback(async (e) => {
    const email = String(e.target.value || '').trim().toLowerCase();
    if (!email || !/\S+@\S+\.\S+/.test(email)) return;
    if (email === lastLookedUpEmail) return;
    setLastLookedUpEmail(email);
    try {
      const fn = httpsCallable(getFunctions(), 'lookupParentProfile');
      const { data } = await fn({ email });
      if (!data?.found || !data.profile) return;
      let didPrefill = false;
      setForm(p => {
        const next = { ...p };
        if (!p.parentName.trim()  && data.profile.parentName)  {
          next.parentName  = data.profile.parentName;
          didPrefill = true;
        }
        if (!p.parentPhone.trim() && data.profile.parentPhone) {
          // Pretty-print whatever the server handed back. The server stores
          // E.164 (+15550000000) which is unfriendly in a form input; the
          // user can still edit it after.
          next.parentPhone = formatUsPhone(data.profile.parentPhone) || data.profile.parentPhone;
          didPrefill = true;
        }
        // Pre-check SMS opt-in for returning parents who previously consented.
        // TCPA is fine with this — express consent carries forward as long
        // as they haven't replied STOP (which flips profile.smsOptIn false
        // via our Twilio inbound webhook).
        if (!p.smsOptIn && data.profile.smsOptIn === true) {
          next.smsOptIn = true;
          didPrefill = true;
        }
        return next;
      });
      if (didPrefill) setShowWelcomeBack(true);
    } catch (err) {
      // Swallow — pre-fill is best-effort.
      console.debug('[lookupParentProfile]', err?.message);
    }
  }, [lastLookedUpEmail]);

  /* ── Phone blur: normalize display + warn on invalid ───
   * Runs on blur from parentPhone OR emergencyPhone. If the input parses as
   * a valid US 10-digit number we reformat it to "(555) 000-0000" so the
   * field looks tidy and matches what Twilio will see. If it can't be
   * parsed and the parent has opted into SMS, we flag it inline — silent
   * ambiguity here would fail-closed later at send time and the parent
   * would never know why they didn't get texts.
   */
  const handlePhoneBlur = useCallback((e) => {
    const { name, value } = e.target;
    const raw = String(value || '').trim();
    if (!raw) return;
    const e164 = toE164US(raw);
    if (e164) {
      const pretty = formatUsPhone(e164);
      if (pretty && pretty !== raw) {
        setForm(p => ({ ...p, [name]: pretty }));
      }
      // Clear any prior error
      if (errors[name]) setErrors(p => ({ ...p, [name]: null }));
    } else if (name === 'parentPhone' && form.smsOptIn) {
      setErrors(p => ({ ...p, parentPhone: t.smsPhoneInvalid }));
    }
  }, [errors, form.smsOptIn, t.smsPhoneInvalid]);

  /* ── Validation ─────────────────────────────────────── */
  const validateStep = useCallback((s) => {
    const e = {};
    if (s === 1) {
      if (!form.playerFirstName.trim()) e.playerFirstName = t.errRequired;
      if (!form.playerLastName.trim())  e.playerLastName  = t.errRequired;
      if (!form.playerDOB)              e.playerDOB       = t.errRequired;
      if (divisions.length > 0 && !form.division) e.division = t.errDivision;
    }
    if (s === 2) {
      if (!form.parentName.trim())  e.parentName  = t.errRequired;
      if (!form.parentEmail.trim() || !/\S+@\S+\.\S+/.test(form.parentEmail))
        e.parentEmail = t.errEmail;
      if (!form.parentPhone.trim())  e.parentPhone  = t.errRequired;
      // If the parent opted into SMS, the phone must be parseable as US.
      // Collecting a consent + an unreachable number = guaranteed CX bug
      // later ("why didn't I get the weather text?").
      else if (form.smsOptIn && !toE164US(form.parentPhone)) {
        e.parentPhone = t.smsPhoneInvalid;
      }
      if (!form.emergencyName.trim())  e.emergencyName  = t.errRequired;
      if (!form.emergencyPhone.trim()) e.emergencyPhone = t.errRequired;
      if (!form.emergencyRelationship) e.emergencyRelationship = t.errRequired;
    }
    // Step 3 (Payment): no required fields
    if (s === 4) {
      const activeWaivers = season?.waivers?.length > 0 ? season.waivers : (season?.waiverText ? [{ name: 'Liability Waiver', text: season.waiverText }] : []);
      const allAgreed = activeWaivers.every((_, i) => form.waiverAgreements[i]);
      if (!allAgreed) e.waiverAgreed = t.errWaivers;
      if (!form.waiverSignedName.trim()) e.waiverSignedName = t.errSignName;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [form, divisions, t]);

  const handleNext = () => {
    setStepAttempted(p => ({ ...p, [step]: true }));
    if (validateStep(step)) {
      toast.dismiss(); // clear any lingering error toasts so they don't bleed into the next step
      let next = step + 1;
      if (next === 3 && !hasPaymentPlan) next = 4; // skip payment step
      setStep(next);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Persist progress. Fire-and-forget — never blocks the UI.
      saveDraft({ lastStep: next }).catch(() => null);
    } else {
      toast.error(t.errFillAll);
    }
  };

  /* ── Save & finish later button ─────────────────────────
   * Persists the current form state and emails a resume link. The button
   * is only visible from step 2 onwards (needs parentEmail). Toast feedback
   * is localized; `saveForLaterState` drives the button label.
   */
  const handleSaveForLater = useCallback(async () => {
    const email = String(form.parentEmail || '').trim().toLowerCase();
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      toast.error(t.saveForLaterNoEmail);
      return;
    }
    setSaveForLaterState('sending');
    try {
      const id = await saveDraft();
      if (!id) {
        // saveDraft returns null only when the email is missing — and we
        // just checked it. Belt + suspenders: bail gracefully.
        toast.error(t.saveForLaterError);
        setSaveForLaterState('idle');
        return;
      }
      const fn = httpsCallable(getFunctions(), 'sendResumeLink');
      await fn({ draftId: id, email });
      setSaveForLaterState('sent');
      toast.success(t.saveForLaterSent);
      // Let the button label revert after a moment so repeat-use works.
      setTimeout(() => setSaveForLaterState('idle'), 6000);
    } catch (err) {
      const code = err?.code || '';
      if (code.includes('resource-exhausted')) {
        toast.error(t.saveForLaterRateLimited);
      } else {
        toast.error(t.saveForLaterError);
      }
      setSaveForLaterState('idle');
    }
  }, [form.parentEmail, saveDraft, t]);

  const handleBack = () => {
    setErrors({});
    let prev = step - 1;
    if (prev === 3 && !hasPaymentPlan) prev = 2; // skip payment step back
    setStep(prev);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /* ── Submit ─────────────────────────────────────────── */
  const onSubmit = async (e) => {
    e.preventDefault();
    // Guard: Enter-key in a text field on steps 1–3 bubbles up to the form
    // and triggers onSubmit even though the user isn't on the waiver step.
    if (step !== 4) return;

    setStepAttempted(p => ({ ...p, 4: true }));
    if (!validateStep(4)) { toast.error(t.errFillAll); return; }

    setSaving(true);
    let docRef = null; // hoisted so catch can delete an orphan doc if checkout fails
    try {
      const p = pricing;
      const amountDue = p.paymentPlan ? p.paymentPlan.firstInstallment : p.finalAmount;
      const isPaid    = amountDue > 0;

      // Best-effort E.164 normalization before write. Server re-normalizes
      // in sendRegistrationConfirmation as a belt-and-suspenders step — if
      // we fail client-side the server fixes it up; if we succeed it's a
      // no-op (idempotent).
      const normalizedParentPhone = toE164US(form.parentPhone) || form.parentPhone.trim();

      // Tiered pricing: stamp the resolved tier so the dashboard can
      // group by tier later + the server has a hint to compare against.
      // Server re-resolves authoritatively in createRegistrationCheckout —
      // these fields are for audit + display, not pricing trust.
      const tierStamp = (isTiered && activeTier)
        ? { pricingTierId: activeTier.id, pricingTierName: activeTier.name }
        : {};

      docRef = await addDoc(collection(db, 'registrations'), {
        teamId, seasonId,
        seasonName: season.name,
        teamName:   team.name,
        // Snapshot the parent's preferred language from the i18n locale
        // active at submit time. Consumed server-side by
        // functions/utils/parentLanguage.resolveParentLanguage so every
        // outbound notification (game reminder, installment email, weather
        // alert, etc.) arrives in the parent's preferred language without
        // requiring them to re-state the preference later.
        // (Part of features/outbound-bilingual-notifications.)
        language: lang,
        ...tierStamp,
        playerFirstName:  form.playerFirstName.trim(),
        playerLastName:   form.playerLastName.trim(),
        playerDOB:        form.playerDOB,
        playerName:       `${form.playerFirstName.trim()} ${form.playerLastName.trim()}`,
        division:         form.division || null,
        parentName:       form.parentName.trim(),
        parentEmail:      form.parentEmail.trim().toLowerCase(),
        parentPhone:      normalizedParentPhone,
        // TCPA audit trail. Only write the consent block if the parent
        // actually checked the box — we never want to look like we recorded
        // opt-in on a form where they didn't.
        smsOptIn: !!form.smsOptIn,
        ...(form.smsOptIn ? {
          smsConsentAt:   serverTimestamp(),
          // Exact disclosure text the parent saw. Locale-stamped so we can
          // prove to a regulator which text was live at consent time.
          smsConsentText: t.smsOptInDisclosure,
          smsConsentLang: lang,
          // smsConsentIp filled in server-side (client can't see its own IP).
          smsConsentIp: null,
          smsConsentUserAgent:
            (typeof navigator !== 'undefined' && navigator.userAgent) || null,
        } : {}),
        emergencyContact: {
          name:         form.emergencyName.trim(),
          phone:        form.emergencyPhone.trim(),
          relationship: form.emergencyRelationship,
        },
        medicalNotes:       form.medicalNotes.trim() || null,
        waiverSignedAt:     serverTimestamp(),
        waiverSignedByName: form.waiverSignedName.trim(),
        waiverVersion:      season.updatedAt?.seconds?.toString() ?? 'v1',
        waiversSigned:      (season.waivers?.length > 0 ? season.waivers : [{ name: 'Liability Waiver' }]).map(w => w.name),
        // E-signature audit trail — snapshot the exact text the parent agreed to,
        // plus the device + (server will add) IP address. ESIGN/UETA require a
        // clear record of what was signed, not just a checkbox.
        waiverTextSnapshot: (season.waivers?.length > 0
          ? season.waivers
          : (season.waiverText ? [{ name: 'Liability Waiver', text: season.waiverText }] : [])
        ).map(w => ({ name: w.name || null, text: w.text || null })),
        waiverSignedUserAgent: (typeof navigator !== 'undefined' && navigator.userAgent) || null,
        waiverSignedIp: null, // filled in server-side by sendRegistrationConfirmation
        pricing: {
          baseAmount:           p.baseAmount,
          earlyBirdApplied:     p.earlyBirdApplied,
          earlyBirdSaving:      p.earlyBirdSaving,
          siblingDiscount:      p.siblingDiscount,
          returningDiscount:    p.returningDiscount,
          financialAidDiscount: 0,
          totalDiscount:        p.totalDiscount,
          subtotalAmount:       p.subtotalAmount,
          processingFeeMode:    p.processingFeeMode,
          processingFee:        p.processingFee,
          finalAmount:          p.finalAmount,
        },
        hasSibling:  form.hasSibling,
        isReturning: form.isReturning,
        paymentPlan: p.paymentPlan ? {
          installments:      p.paymentPlan.installments,
          installmentAmount: p.paymentPlan.installmentAmount,
          firstInstallment:  p.paymentPlan.firstInstallment,
          frequency:         p.paymentPlan.frequency,
          chargeDay:         p.paymentPlan.chargeDay ?? null,
          // Full schedule used by Phase 2 auto-charge scheduler
          schedule: p.paymentPlan.schedule.map((row, i) => ({
            dueDate: row.dueDate, // Firestore will convert Date → Timestamp
            amount:  row.amount,
            status:  i === 0 ? 'pending_payment' : 'pending',
          })),
        } : null,
        installmentsTotal: p.paymentPlan ? p.paymentPlan.installments : 1,
        installmentsPaid:  0,
        amountDue,
        status:    isPaid ? 'pending_payment' : 'confirmed',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Mark any associated draft as promoted so the abandonment-reminder
      // cron stops chasing this parent. Best-effort — a failed update here
      // just means one extra reminder email at worst.
      if (draftId) {
        updateDoc(doc(db, 'registrationDrafts', draftId), {
          promotedToRegistrationId: docRef.id,
          updatedAt: serverTimestamp(),
        }).catch(() => {});
      }

      if (isPaid) {
        // For paid registrations: just get the checkout URL and redirect.
        // The confirmation email is now sent by the Stripe webhook
        // (functions/modules/billing.js → checkout.session.completed
        // handler) AFTER the parent actually pays. May 2026 fix —
        // pre-fix, the email fired the moment Stripe returned a URL,
        // before the parent had a chance to abandon at the Stripe page.
        // Parents who cancelled at Stripe still got "you're confirmed"
        // emails; that bug is gone now that the email is gated on
        // payment success.
        toast.loading(t.redirectingPayment);
        const fn = httpsCallable(getFunctions(), 'createRegistrationCheckout');
        const result = await fn({
          registrationId: docRef.id,
          seasonId, teamId,
          successUrl: `${window.location.origin}/register/${teamId}/${seasonId}/success?rid=${docRef.id}`,
          cancelUrl:  `${window.location.origin}/register/${teamId}/${seasonId}`,
        });
        window.location.href = result.data.url;
      } else {
        // Free registration: committed immediately, send confirmation
        // here directly. Free flows don't hit Stripe so no webhook
        // fires; the client-side call is the only path.
        httpsCallable(getFunctions(), 'sendRegistrationConfirmation')({ registrationId: docRef.id })
          .catch(err => console.warn('[RegisterPage] confirmation email skipped', err));
        setStatus('done');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      console.error('[RegisterPage] submit', err);
      toast.dismiss(); // clear any lingering "Redirecting…" loading toast

      // Delete the orphan registration doc so repeated attempts don't pile up
      // in the dashboard as duplicate "Pending Payment" rows.
      if (docRef) {
        deleteDoc(docRef).catch(() => {});
        docRef = null;
      }

      const code = err?.code || '';
      const lowerMsg = (err.message || '').toLowerCase();
      const isSiblingError = code === 'functions/failed-precondition' &&
        lowerMsg.includes('sibling');
      // Tier capacity filled (or shifted) between page-load and submit —
      // refresh the page so the parent re-resolves against the new state
      // (showing them the next active tier price, or the closed empty state).
      const isTierShiftError = code === 'functions/failed-precondition' &&
        (lowerMsg.includes('pricing tier') || lowerMsg.includes('tier_changed'));
      if (isSiblingError) {
        // Show an actionable modal instead of a toast — parent can choose to
        // remove the discount and continue right now, or register the sibling first.
        setShowSiblingModal(true);
      } else if (isTierShiftError) {
        toast.error(
          err.message || 'The pricing tier just changed — refreshing so you see the current price.',
          { duration: 6000 }
        );
        // Hard reload — the simplest way to re-pull tier counts and re-render
        // the whole flow against the new active tier.
        setTimeout(() => window.location.reload(), 1500);
      } else {
        const isUserFacing = code.startsWith('functions/') && !code.includes('internal');
        toast.error(
          isUserFacing ? err.message : 'Something went wrong. Please try again.',
          { duration: isUserFacing ? 8000 : 4000 }
        );
      }
      setSaving(false);
    }
  };

  /* ─────────────────────────────────────────────────── */
  /* Status screens                                      */
  /* ─────────────────────────────────────────────────── */
  if (status === 'loading' || hydrating) {
    return (
      <div className="min-h-screen bg-[#f7faf5] flex flex-col items-center justify-center gap-3">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-[#5a8a1f]" />
        {hydrating && status !== 'loading' && (
          <p className="text-sm text-zinc-500">{t.resumeLoadingTitle}</p>
        )}
      </div>
    );
  }
  if (status === 'error') {
    return (
      <StatusScreen icon="❌" title={t.notFoundTitle} body={t.notFoundBody} />
    );
  }
  if (status === 'closed') {
    return (
      <StatusScreen icon="📅" title={t.closedTitle}
        body={t.closedBody(season?.name ?? 'this season', team?.name ?? 'your team')} />
    );
  }
  if (status === 'full') {
    return (
      <StatusScreen icon="🎽" title={t.fullTitle}
        body={t.fullBody(season?.name ?? 'this season', team?.name ?? 'your team')} />
    );
  }
  if (status === 'done') {
    return (
      <SuccessScreen
        team={team} season={season}
        playerName={form.playerFirstName}
        lang={lang}
      />
    );
  }
  // Tiered pricing closed state: when every tier is either expired or its
  // cap has filled, we have no price to charge — fall back to the same
  // "registration closed" empty state coaches would see for a manually
  // closed season. Same component so the parent gets a consistent UX.
  if (isTiered && !activeTier) {
    return (
      <StatusScreen
        icon="📅"
        title={t.closedTitle}
        body={t.closedBody(season?.name ?? 'this season', team?.name ?? 'your team')}
      />
    );
  }

  /* ─────────────────────────────────────────────────── */
  /* Form                                                */
  /* ─────────────────────────────────────────────────── */
  const regClose  = toDate(season.registrationClose);
  const isUrgent  = spotsLeft !== null && spotsLeft <= 5;
  const sa1 = stepAttempted[1];
  const sa2 = stepAttempted[2];
  const sa4 = stepAttempted[4];

  // Indicator labels and current-step mapping
  const indicatorLabels = hasPaymentPlan
    ? [t.step1, t.step2, t.step3, t.step4]
    : [t.step1, t.step2, t.step4];
  const indicatorCurrent = hasPaymentPlan ? step : (step === 4 ? 3 : step);

  // Amount due on final step
  const amountDue = pricing
    ? (pricing.paymentPlan ? pricing.paymentPlan.firstInstallment : pricing.finalAmount)
    : 0;

  // Price without sibling discount — shown in the modal "Remove discount" button
  const noSiblingPricing = season && form.hasSibling
    ? calculatePrice(season, { ...form, hasSibling: false })
    : pricing;
  const noSiblingAmountDue = noSiblingPricing
    ? (noSiblingPricing.paymentPlan ? noSiblingPricing.paymentPlan.firstInstallment : noSiblingPricing.finalAmount)
    : 0;

  return (
    <div className="min-h-screen bg-[#f7faf5]">

      {/* Language toggle — same pill pattern as the rest of the app's
          public-facing surfaces, lime accent on hover so it ties into
          the system without screaming for attention. */}
      <div className="flex justify-end px-4 pt-4 max-w-xl mx-auto">
        <button
          onClick={() => setLang(l => l === 'en' ? 'es' : 'en')}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-zinc-200 text-xs font-bold uppercase text-zinc-600 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] hover:border-[#b5f03d] hover:text-[#5a8a1f] transition-colors"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
        >
          <span>{lang === 'en' ? '🇲🇽' : '🇺🇸'}</span>
          {lang === 'en' ? 'Español' : 'English'}
        </button>
      </div>

      {/* Pitch-dark hero — replaces the previous gradient/center-aligned
          plain header. The dark background + lime grid overlay reads as
          a premium first-impression and matches the in-app team header
          treatment in EventDetailPage / ClubDashboard. Lime-ringed logo
          medallion is the anchor. */}
      <div className="px-4 pt-5 pb-6 max-w-xl mx-auto">
        <div
          className="relative overflow-hidden rounded-2xl bg-[#0d1f14] px-5 py-6 shadow-[0_2px_6px_0_rgba(13,31,20,0.18)]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(181,240,61,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(181,240,61,0.06) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        >
          <div className="relative flex flex-col items-center text-center">
            {team?.logoUrl ? (
              <img
                src={team.logoUrl}
                alt={team.name}
                className="h-16 w-16 rounded-2xl object-cover ring-2 ring-[#b5f03d]/40 bg-white p-1 mb-3"
              />
            ) : (
              <div className="h-16 w-16 rounded-2xl bg-[#b5f03d] ring-2 ring-[#b5f03d]/30 flex items-center justify-center mb-3">
                <span
                  className="text-2xl font-bold text-[#0d1f14] uppercase"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  {team?.name?.[0] ?? '⚽'}
                </span>
              </div>
            )}
            <p
              className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#b5f03d]"
              style={{ fontFamily: HEAD_FONT }}
            >
              {t.regEyebrow}
            </p>
            <h1
              className="text-2xl font-bold uppercase tracking-tight text-white mt-0.5"
              style={{ fontFamily: HEAD_FONT }}
            >
              {team?.name}
            </h1>
            <p className="text-[#b5f03d]/90 font-semibold mt-1 text-sm">{season?.name}</p>

            {/* Info pills — repainted into the lime/pitch palette. Urgent
                spots-left stays red because that signal needs to remain
                loud regardless of brand color. */}
            <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
              {pricing?.earlyBirdApplied && (
                <span
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#b5f03d] text-[#0d1f14] text-[11px] font-bold uppercase"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  <SparklesIcon className="h-3 w-3" />
                  {t.earlyBird(pricing.earlyBirdSaving)}
                </span>
              )}
              {regClose && (
                <span
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/10 text-zinc-200 text-[11px] font-medium uppercase tracking-wide"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  <CalendarDaysIcon className="h-3 w-3" />
                  {t.closes} {format(regClose, 'MMM d, yyyy', lang === 'es' ? { locale: dateLocaleEs } : undefined)}
                </span>
              )}
              {spotsLeft !== null && (
                <span className={clsx(
                  'inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-bold uppercase',
                  isUrgent ? 'bg-red-500 text-white' : 'bg-white/10 text-zinc-200'
                )}
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  <UserGroupIcon className="h-3 w-3" />
                  {isUrgent ? t.spotsLeft(spotsLeft) : t.spotsRemaining(spotsLeft)}
                </span>
              )}
              {pricing && (
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#b5f03d]/15 text-[#b5f03d] border border-[#b5f03d]/40 text-[11px] font-bold uppercase"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  <CurrencyDollarIcon className="h-3 w-3" />
                  {pricing.totalDiscount > 0 && pricing.finalAmount < pricing.baseAmount ? (
                    <><s className="text-[#b5f03d]/50 font-normal">{fmtMoney(pricing.baseAmount)}</s> {pricing.finalAmount ? fmtMoney(pricing.finalAmount) : t.freeBadge}</>
                  ) : (pricing.finalAmount ? fmtMoney(pricing.finalAmount) : t.freeBadge)}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 pb-20 space-y-5">

        {/* What's Included — same lime-checkmark + lime-accent header
            as the rest of the card system. Pre-step content, sets
            expectations before the form starts. */}
        {season.whatsIncluded?.length > 0 && (
          <div className="bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-3.5 border-b border-zinc-100">
              <span className="text-base">✅</span>
              <h3
                className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                style={{ fontFamily: HEAD_FONT }}
              >
                {lang === 'en' ? "What's Included" : "¿Qué Incluye?"}
              </h3>
            </div>
            <div className="px-5 py-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {season.whatsIncluded.map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <CheckIcon className="h-4 w-4 text-[#5a8a1f] flex-shrink-0 mt-0.5 stroke-[2.5]" />
                  <span className="text-sm text-zinc-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step indicator */}
        <StepIndicator labels={indicatorLabels} current={indicatorCurrent} />

        {/* Active pricing tier banner — only shown for tiered seasons.
            Lime/pitch palette, mirrors the rest of the parent-facing
            surface. Also surfaces an urgency cue when the tier is
            ending soon or close to capacity, so parents who are on the
            fence have a reason to commit before the price jumps. */}
        {isTiered && activeTier && (
          <TierBanner
            tier={activeTier}
            tierFillCounts={tierFillCounts}
            lang={lang}
          />
        )}

        {/* Resume-from-email banner. Shown once after hydrating from
            `?resume=<id>`; dismisses itself on any click. Lime treatment
            keeps it part of the system rather than a separate emerald
            tone. */}
        {showResumeBanner && (
          <div
            role="button"
            tabIndex={0}
            onClick={() => setShowResumeBanner(false)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowResumeBanner(false); }}
            className="mb-4 flex items-start gap-2 rounded-xl border border-[#b5f03d]/40 bg-[#b5f03d]/10 px-4 py-3 text-sm text-[#3d6010] cursor-pointer"
          >
            <SparklesIcon className="h-5 w-5 flex-shrink-0 mt-0.5 text-[#5a8a1f]" />
            <span>{t.resumeBanner}</span>
          </div>
        )}

        {/* Form */}
        <form
          onSubmit={onSubmit}
          onKeyDown={(e) => {
            // Belt-and-suspenders against Enter-key in an input field bubbling up to
            // the form and triggering onSubmit before the user has reached step 4.
            // Without this, typing Enter on a text field (common on mobile keyboards
            // that show a "Go"/"Send" action) would fire final validation and
            // surface the "please fill in all required fields" toast.
            if (e.key === 'Enter' && step !== 4 && e.target.tagName !== 'TEXTAREA') {
              e.preventDefault();
            }
          }}
          noValidate
        >

          {/* ── Step 1: Player ──────────────────────────── */}
          {step === 1 && (
            <div className="space-y-4">
              <Card title={t.playerInfo} icon="⚽">
                <div className="grid grid-cols-2 gap-4">
                  <Field>
                    <CatalystLabel htmlFor="playerFirstName">{t.firstName} <Req /></CatalystLabel>
                    <Input id="playerFirstName" name="playerFirstName" value={form.playerFirstName}
                      onChange={handleChange} invalid={!!(sa1 && errors.playerFirstName)}
                      disabled={saving} placeholder="Alex" />
                    {sa1 && errors.playerFirstName && <ErrorMessage>{errors.playerFirstName}</ErrorMessage>}
                  </Field>
                  <Field>
                    <CatalystLabel htmlFor="playerLastName">{t.lastName} <Req /></CatalystLabel>
                    <Input id="playerLastName" name="playerLastName" value={form.playerLastName}
                      onChange={handleChange} invalid={!!(sa1 && errors.playerLastName)}
                      disabled={saving} placeholder="Johnson" />
                    {sa1 && errors.playerLastName && <ErrorMessage>{errors.playerLastName}</ErrorMessage>}
                  </Field>
                </div>
                <Field>
                  <CatalystLabel htmlFor="playerDOB">{t.dob} <Req /></CatalystLabel>
                  <Input id="playerDOB" name="playerDOB" type="date" value={form.playerDOB}
                    onChange={handleChange} invalid={!!(sa1 && errors.playerDOB)}
                    disabled={saving} className="max-w-xs" />
                  {sa1 && errors.playerDOB && <ErrorMessage>{errors.playerDOB}</ErrorMessage>}
                </Field>

                {/* Division as tap cards — lime-bordered when picked,
                    matches the same selectable-card pattern used in the
                    in-app event picker + payment plan picker. */}
                {divisions.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-[#0d1f14] mb-2">{t.division} <Req /></p>
                    <div className="grid grid-cols-2 gap-2">
                      {divisions.map(d => {
                        const price = season.divisionPricing[d];
                        const sel   = form.division === d;
                        return (
                          <button key={d} type="button" disabled={saving}
                            onClick={() => {
                              setForm(f => ({ ...f, division: d }));
                              if (errors.division) setErrors(p => ({ ...p, division: null }));
                            }}
                            className={clsx(
                              'text-left px-4 py-3 rounded-xl border-2 transition-all',
                              sel ? 'border-[#b5f03d] bg-[#b5f03d]/10' : 'border-zinc-200 bg-white hover:border-[#b5f03d]/40'
                            )}
                          >
                            <p
                              className={clsx('text-sm font-bold uppercase', sel ? 'text-[#0d1f14]' : 'text-[#0d1f14]')}
                              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                            >
                              {d}
                            </p>
                            <p className={clsx('text-xs mt-0.5', sel ? 'text-[#5a8a1f]' : 'text-zinc-500')}>{fmtMoney(price)}</p>
                          </button>
                        );
                      })}
                    </div>
                    {sa1 && errors.division && <p className="mt-1.5 text-xs text-red-500">{errors.division}</p>}
                  </div>
                )}
              </Card>

              {/* Discounts */}
              {(season.siblingDiscountEnabled || season.returningDiscountEnabled) && (
                <Card title={t.discounts} icon="🏷️">
                  <div className="space-y-2">
                    {season.siblingDiscountEnabled && (
                      <DiscountToggle
                        name="hasSibling" checked={form.hasSibling}
                        onChange={handleChange} disabled={saving}
                        label={t.siblingCheck} saves={t.saves(season.siblingDiscountAmount)}
                      />
                    )}
                    {season.returningDiscountEnabled && (
                      <DiscountToggle
                        name="isReturning" checked={form.isReturning}
                        onChange={handleChange} disabled={saving}
                        label={t.returningCheck} saves={t.saves(season.returningDiscountAmount)}
                      />
                    )}
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* ── Step 2: Parent & Emergency ──────────────── */}
          {step === 2 && (
            <div className="space-y-4">
              {showWelcomeBack && (
                <div className="rounded-xl border border-[#b5f03d]/40 bg-[#b5f03d]/10 px-4 py-3 text-sm text-[#3d6010] flex items-start gap-2">
                  <SparklesIcon className="h-5 w-5 flex-shrink-0 mt-0.5 text-[#5a8a1f]" />
                  <span>{t.welcomeBack}</span>
                </div>
              )}
              <Card title={t.parentTitle} icon="👤">
                {/* Message-language choice — the most important preference
                    on this form: registration.language routes every future
                    team message, digest, and alert. It used to be captured
                    only implicitly from the top-corner locale pill (easy
                    to miss). These chips make it explicit, and the label
                    renders in BOTH languages on purpose so a
                    Spanish-speaking parent working through the English
                    form cannot miss it. Selecting a chip flips the whole
                    page locale — the same state the submit snapshot
                    reads — so the choice and the UI can never drift. */}
                <div className="mb-4 rounded-xl border border-[#b5f03d]/50 bg-[#b5f03d]/10 p-3.5">
                  <p className="text-sm font-semibold text-[#0d1f14] leading-snug">
                    What language should team messages arrive in?
                  </p>
                  <p className="text-sm font-semibold text-[#0d1f14] leading-snug mb-2.5">
                    ¿En qué idioma quieres recibir los mensajes del equipo?
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[['en', 'English'], ['es', 'Español']].map(([code, label]) => (
                      <button key={code} type="button" disabled={saving}
                        onClick={() => setLang(code)}
                        className={clsx(
                          'px-4 py-2 rounded-full text-sm font-bold uppercase border-2 transition-all',
                          lang === code
                            ? 'border-[#b5f03d] bg-[#b5f03d]/20 text-[#0d1f14]'
                            : 'border-zinc-200 bg-white text-zinc-600 hover:border-[#b5f03d]/40 hover:text-[#0d1f14]'
                        )}
                        style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <Field>
                  <CatalystLabel htmlFor="parentName">{t.fullName} <Req /></CatalystLabel>
                  <Input id="parentName" name="parentName" value={form.parentName}
                    onChange={handleChange} invalid={!!(sa2 && errors.parentName)}
                    disabled={saving} placeholder="Jamie Johnson" />
                  {sa2 && errors.parentName && <ErrorMessage>{errors.parentName}</ErrorMessage>}
                </Field>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field>
                    <CatalystLabel htmlFor="parentEmail">{t.email} <Req /></CatalystLabel>
                    <Input id="parentEmail" name="parentEmail" type="email" value={form.parentEmail}
                      onChange={handleChange} onBlur={handleParentEmailBlur}
                      invalid={!!(sa2 && errors.parentEmail)}
                      disabled={saving} placeholder="jamie@email.com" />
                    {sa2 && errors.parentEmail && <ErrorMessage>{errors.parentEmail}</ErrorMessage>}
                  </Field>
                  <Field>
                    <CatalystLabel htmlFor="parentPhone">{t.phone} <Req /></CatalystLabel>
                    <Input id="parentPhone" name="parentPhone" type="tel" value={form.parentPhone}
                      onChange={handleChange} onBlur={handlePhoneBlur}
                      invalid={!!(sa2 && errors.parentPhone)}
                      disabled={saving} placeholder="(555) 000-0000" autoComplete="tel" />
                    {sa2 && errors.parentPhone && <ErrorMessage>{errors.parentPhone}</ErrorMessage>}
                  </Field>
                </div>

                {/* TCPA SMS consent — unchecked by default. Disclosure text
                    is the exact language snapshotted onto the registration
                    doc so we can prove what the parent saw at consent time. */}
                <div className="mt-1 rounded-xl border border-zinc-200 bg-zinc-50 p-3">
                  <label className="flex items-start gap-2.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      name="smsOptIn"
                      checked={!!form.smsOptIn}
                      onChange={handleChange}
                      disabled={saving}
                      className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-[#5a8a1f] focus:ring-[#b5f03d]"
                    />
                    <span className="text-sm text-[#0d1f14] leading-snug">
                      {t.smsOptInLabel}
                    </span>
                  </label>
                  <p className="mt-2 pl-6 text-[11px] leading-relaxed text-zinc-500">
                    {t.smsOptInDisclosure}
                  </p>
                </div>
              </Card>

              <Card title={t.emergencyTitle} icon="🆘">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field>
                    <CatalystLabel htmlFor="emergencyName">{t.fullName} <Req /></CatalystLabel>
                    <Input id="emergencyName" name="emergencyName" value={form.emergencyName}
                      onChange={handleChange} invalid={!!(sa2 && errors.emergencyName)}
                      disabled={saving} placeholder="Full name" />
                    {sa2 && errors.emergencyName && <ErrorMessage>{errors.emergencyName}</ErrorMessage>}
                  </Field>
                  <Field>
                    <CatalystLabel htmlFor="emergencyPhone">{t.phone} <Req /></CatalystLabel>
                    <Input id="emergencyPhone" name="emergencyPhone" type="tel" value={form.emergencyPhone}
                      onChange={handleChange} onBlur={handlePhoneBlur}
                      invalid={!!(sa2 && errors.emergencyPhone)}
                      disabled={saving} placeholder="(555) 000-0000" autoComplete="tel" />
                    {sa2 && errors.emergencyPhone && <ErrorMessage>{errors.emergencyPhone}</ErrorMessage>}
                  </Field>
                </div>
                {/* Relationship as pill buttons — same lime selected
                    state as the division pills above for consistency. */}
                <div>
                  <p className="text-sm font-semibold text-[#0d1f14] mb-2">{t.relationship} <Req /></p>
                  <div className="flex flex-wrap gap-2">
                    {RELATIONSHIPS.map((r, i) => (
                      <button key={r} type="button" disabled={saving}
                        onClick={() => {
                          setForm(f => ({ ...f, emergencyRelationship: r }));
                          if (errors.emergencyRelationship) setErrors(p => ({ ...p, emergencyRelationship: null }));
                        }}
                        className={clsx(
                          'px-3 py-1.5 rounded-full text-sm font-bold uppercase border-2 transition-all',
                          form.emergencyRelationship === r
                            ? 'border-[#b5f03d] bg-[#b5f03d]/10 text-[#0d1f14]'
                            : 'border-zinc-200 text-zinc-600 hover:border-[#b5f03d]/40 hover:text-[#0d1f14]'
                        )}
                        style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                      >
                        {lang === 'es' ? RELATIONSHIPS_ES[i] : r}
                      </button>
                    ))}
                  </div>
                  {sa2 && errors.emergencyRelationship && (
                    <p className="mt-1.5 text-xs text-red-500">{errors.emergencyRelationship}</p>
                  )}
                </div>
              </Card>

              <Card title={t.medicalTitle} subtitle={t.medicalSub} icon="🩺">
                <Textarea id="medicalNotes" name="medicalNotes" rows={3} value={form.medicalNotes}
                  onChange={handleChange} disabled={saving} placeholder={t.medicalPlaceholder} />
              </Card>
            </div>
          )}

          {/* ── Step 3: Payment Plan ─────────────────────── */}
          {step === 3 && hasPaymentPlan && (
            <div className="space-y-4">
              <Card title={t.paymentTitle} icon="💳">
                <div className="space-y-3">
                  {/* Pay in full */}
                  <PaymentCard
                    selected={form.paymentPlan === null}
                    onSelect={() => setForm(f => ({ ...f, paymentPlan: null }))}
                    title={t.payFull} subtitle={t.payFullSub}
                    badge={null} scheduleLabel={t.paySchedule} hideLabel={t.hideSchedule}
                    amount={fmtMoney(pricing.finalAmount)} amountLabel={t.totalLabel}
                    schedule={null} disabled={saving}
                    paymentDueLabel={t.paymentDue}
                  />
                  {/* Installment options */}
                  {installmentOptions.map((n, idx) => {
                    const pp     = calculatePrice(season, { ...form, paymentPlan: n });
                    const first  = pp.paymentPlan.firstInstallment;
                    const rest   = pp.paymentPlan.installmentAmount;
                    const dateLocale = lang === 'es' ? { locale: es } : undefined;
                    const dateFmt = lang === 'es' ? "d 'de' MMM" : 'MMM d';
                    // Build schedule preview from the real dated schedule
                    const schedule = pp.paymentPlan.schedule.map((row, i) => ({
                      label: i === 0 ? t.today : format(row.dueDate, dateFmt, dateLocale),
                      amount: row.amount,
                    }));
                    return (
                      <PaymentCard
                        key={n}
                        selected={form.paymentPlan === n}
                        onSelect={() => setForm(f => ({ ...f, paymentPlan: n }))}
                        title={t.installments(n)}
                        subtitle={t.installmentsSub(first, rest, n)}
                        badge={idx === 0 ? t.mostPopular : null}
                        amount={fmtMoney(first)} amountLabel={t.firstPayLabel}
                        schedule={schedule} scheduleLabel={t.paySchedule} hideLabel={t.hideSchedule}
                        disabled={saving} paymentDueLabel={t.paymentDue}
                      />
                    );
                  })}
                </div>

                {/* Discount / fee breakdown — emerald reads as savings,
                    pitch text for the final total so it pops as the most
                    important number on the receipt. */}
                {(pricing.totalDiscount > 0 || pricing.processingFee > 0) && (
                  <div className="mt-4 pt-4 border-t border-zinc-100 space-y-1.5 text-xs text-zinc-600">
                    <div className="flex justify-between">
                      <span>{t.basePrice}</span><span>{fmtMoney(pricing.baseAmount)}</span>
                    </div>
                    {pricing.earlyBirdSaving > 0 && (
                      <div className="flex justify-between text-emerald-700 font-semibold">
                        <span>{t.earlyBirdDiscount}</span><span>−{fmtMoney(pricing.earlyBirdSaving)}</span>
                      </div>
                    )}
                    {pricing.siblingDiscount > 0 && (
                      <div className="flex justify-between text-emerald-700 font-semibold">
                        <span>{t.siblingDiscount}</span><span>−{fmtMoney(pricing.siblingDiscount)}</span>
                      </div>
                    )}
                    {pricing.returningDiscount > 0 && (
                      <div className="flex justify-between text-emerald-700 font-semibold">
                        <span>{t.returningDiscount}</span><span>−{fmtMoney(pricing.returningDiscount)}</span>
                      </div>
                    )}
                    {pricing.processingFee > 0 && (
                      <>
                        {pricing.totalDiscount > 0 && (
                          <div className="flex justify-between text-zinc-700 pt-1 border-t border-zinc-100">
                            <span>{t.subtotal}</span><span>{fmtMoney(pricing.subtotalAmount)}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span title={t.processingFeeHint}>{t.processingFee}</span>
                          <span>+{fmtMoney(pricing.processingFee)}</span>
                        </div>
                      </>
                    )}
                    <div
                      className="flex justify-between font-bold text-[#0d1f14] pt-1.5 border-t border-zinc-100 text-sm"
                      style={{ fontFamily: HEAD_FONT }}
                    >
                      <span>{t.total}</span><span>{fmtMoney(pricing.finalAmount)}</span>
                    </div>
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* ── Step 4: Waiver ──────────────────────────── */}
          {step === 4 && (
            <div className="space-y-4">
              {/* Multiple waivers — one card per waiver */}
              {(() => {
                const activeWaivers = season.waivers?.length > 0
                  ? season.waivers
                  : (season.waiverText ? [{ name: lang === 'en' ? 'Liability Waiver' : 'Exención de Responsabilidad', text: season.waiverText }] : []);
                return activeWaivers.map((w, i) => (
                  <Card key={i} title={w.name} icon={i === 0 ? '📋' : '📄'}>
                    <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 max-h-48 overflow-y-auto text-xs text-zinc-700 leading-relaxed whitespace-pre-wrap">
                      {w.text || (lang === 'en' ? 'No waiver text provided.' : 'No hay texto de exención.')}
                    </div>
                    <label className={clsx(
                      'flex items-start gap-3 cursor-pointer rounded-xl border-2 p-4 transition-all',
                      form.waiverAgreements[i] ? 'border-[#b5f03d] bg-[#b5f03d]/10' : 'border-zinc-200 hover:border-[#b5f03d]/40'
                    )}>
                      <input type="checkbox"
                        checked={!!form.waiverAgreements[i]}
                        onChange={e => setForm(f => ({ ...f, waiverAgreements: { ...f.waiverAgreements, [i]: e.target.checked } }))}
                        disabled={saving}
                        className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-[#5a8a1f] focus:ring-[#b5f03d]" />
                      <span className="text-sm text-[#0d1f14] leading-snug">{t.waiverCheck}</span>
                    </label>
                  </Card>
                ));
              })()}

              {/* Single signature for all waivers */}
              <Card title={lang === 'en' ? 'Electronic Signature' : 'Firma Electrónica'} icon="✍️">
                {sa4 && errors.waiverAgreed && (
                  <p className="text-xs text-red-500 -mt-1 mb-2">{errors.waiverAgreed}</p>
                )}

                {/* ESIGN/UETA disclosure — required for e-signatures to be legally binding */}
                <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-3 mb-3">
                  <p className="text-[11px] text-zinc-600 leading-relaxed">{t.esignDisclosure}</p>
                </div>

                <Field>
                  <CatalystLabel htmlFor="waiverSignedName">{t.waiverSign} <Req /></CatalystLabel>
                  <Input id="waiverSignedName" name="waiverSignedName" value={form.waiverSignedName}
                    onChange={handleChange} invalid={!!(sa4 && errors.waiverSignedName)}
                    disabled={saving} placeholder={t.waiverSignPlaceholder}
                    className="max-w-sm font-medium" />
                  {sa4 && errors.waiverSignedName && <ErrorMessage>{errors.waiverSignedName}</ErrorMessage>}
                  <Description>{t.waiverSignSub}</Description>
                </Field>
                <p className="text-xs text-zinc-500">
                  {lang === 'en'
                    ? `Your signature above applies to all ${(season.waivers?.length || 1)} waiver${(season.waivers?.length || 1) !== 1 ? 's' : ''} listed above.`
                    : `Su firma aplica a todas las exenciones listadas arriba.`}
                </p>
              </Card>

              {/* Final amount banner — pitch dark with lime accents.
                  This is the moment-of-truth visual right before the
                  parent submits + pays. Pitch + lime reads as premium
                  "you're checking out" rather than the previous flat
                  blue rectangle. */}
              {pricing && pricing.finalAmount > 0 && (
                <div
                  className="relative overflow-hidden rounded-2xl bg-[#0d1f14] p-5 text-white shadow-[0_2px_6px_0_rgba(13,31,20,0.18)]"
                  style={{
                    backgroundImage:
                      'linear-gradient(rgba(181,240,61,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(181,240,61,0.06) 1px, transparent 1px)',
                    backgroundSize: '32px 32px',
                  }}
                >
                  <div className="relative flex justify-between items-center">
                    <div>
                      <p
                        className="text-[#b5f03d] text-[11px] font-bold uppercase tracking-[1.4px] mb-0.5"
                        style={{ fontFamily: HEAD_FONT }}
                      >
                        {form.paymentPlan ? t.firstOf(form.paymentPlan) : t.dueToday}
                      </p>
                      <p
                        className="text-3xl font-bold"
                        style={{ fontFamily: HEAD_FONT }}
                      >
                        {fmtMoney(amountDue)}
                      </p>
                    </div>
                    <div className="h-12 w-12 rounded-2xl bg-[#b5f03d] flex items-center justify-center flex-shrink-0">
                      <CreditCardIcon className="h-6 w-6 text-[#0d1f14]" />
                    </div>
                  </div>
                  {pricing.totalDiscount > 0 && (
                    <p className="relative text-[#b5f03d]/90 text-xs mt-2">{t.savingWith(pricing.totalDiscount)}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Navigation — primary CTA in lime to match the rest of the
              app. Submit button stays full-width-ish on mobile via
              min-width so the cost preview inside is always visible. */}
          <div className="mt-6 flex items-center justify-between gap-4">
            {step > 1 ? (
              <button type="button" onClick={handleBack} disabled={saving}
                className="flex items-center gap-1.5 text-sm font-bold uppercase text-zinc-500 hover:text-[#0d1f14] transition-colors disabled:opacity-40"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                <ChevronLeftIcon className="h-4 w-4" />{t.back}
              </button>
            ) : <div />}

            {step < 4 ? (
              <button type="button" onClick={handleNext} disabled={saving}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] font-bold text-sm uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] active:scale-[0.97] transition-all disabled:opacity-50"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                {t.next}<ChevronRightIcon className="h-4 w-4" />
              </button>
            ) : (
              <button type="submit" disabled={saving}
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] font-bold text-sm uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] active:scale-[0.97] transition-all disabled:opacity-60 min-w-[220px]"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                {saving ? (
                  <><ArrowPathIcon className="h-5 w-5 animate-spin" />{t.submitting}</>
                ) : (
                  <>
                    {/* Lock icon only on paid flows — reinforces "you're
                        about to be redirected to a secure Stripe page,
                        not staying on Scrimly to enter card details
                        directly." Free flows keep a clean text-only
                        button. */}
                    {amountDue > 0 && <LockClosedIcon className="h-4 w-4" />}
                    {t.submitBtn(amountDue)}
                  </>
                )}
              </button>
            )}
          </div>

          {/* Save & finish later — only shown from step 2 onwards (needs email)
              and hidden once submission is in flight. */}
          {step >= 2 && step < 4 && !saving && (
            <div className="mt-5 pt-4 border-t border-zinc-100 text-center">
              <button
                type="button"
                onClick={handleSaveForLater}
                disabled={saveForLaterState === 'sending'}
                className="text-sm font-bold uppercase text-[#5a8a1f] hover:text-[#3d6010] hover:underline transition-colors disabled:opacity-60 disabled:no-underline"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                {saveForLaterState === 'sending' ? t.saveForLaterSending
                  : saveForLaterState === 'sent'   ? `✓ ${t.saveForLaterSent}`
                  : t.saveForLater}
              </button>
              <p className="mt-1.5 text-xs text-zinc-500">{t.saveForLaterHelp}</p>
            </div>
          )}

          {step === 4 && (
            <p className="mt-3 text-center text-xs text-zinc-500">
              {t.secureNote(team?.name ?? 'the team')}
            </p>
          )}
        </form>
      </div>

      {/* ── Sibling discount verification modal ─────────── */}
      {showSiblingModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowSiblingModal(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white shadow-2xl overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start gap-3 px-5 py-4 border-b border-zinc-100">
              <div className="mt-0.5 h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <ExclamationTriangleIcon className="h-4 w-4 text-amber-600" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-zinc-900 leading-tight">{t.siblingModalTitle}</h3>
              </div>
            </div>

            {/* Body */}
            <div className="px-5 py-4 space-y-2">
              <p className="text-sm text-zinc-600 leading-relaxed">{t.siblingModalBody}</p>
              <p className="text-xs text-zinc-400 leading-relaxed">{t.siblingModalHint}</p>
            </div>

            {/* Actions */}
            <div className="px-5 pb-5 pt-1 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => {
                  setForm(f => ({ ...f, hasSibling: false }));
                  setShowSiblingModal(false);
                }}
                className="w-full py-3 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] text-sm font-bold uppercase active:scale-[0.97] transition-all shadow-[0_1px_2px_0_rgba(13,31,20,0.08)]"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                {t.siblingModalRemove(noSiblingAmountDue)}
              </button>
              <button
                type="button"
                onClick={() => setShowSiblingModal(false)}
                className="w-full py-2.5 rounded-xl border border-zinc-200 bg-white text-sm text-zinc-600 font-bold uppercase hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 hover:text-[#0d1f14] transition-colors"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                {t.siblingModalRegisterFirst}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Sub-components                                              */
/* ─────────────────────────────────────────────────────────── */

function StepIndicator({ labels, current }) {
  return (
    <div className="flex items-center px-1">
      {labels.map((label, i) => {
        const num    = i + 1;
        const done   = num < current;
        const active = num === current;
        return (
          <React.Fragment key={i}>
            <div className="flex flex-col items-center min-w-0">
              <div className={clsx(
                'h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold transition-all',
                done    ? 'bg-[#b5f03d] text-[#0d1f14] shadow-[0_1px_2px_0_rgba(13,31,20,0.08)]'
                : active ? 'bg-white border-2 border-[#b5f03d] text-[#5a8a1f] shadow-sm ring-4 ring-[#b5f03d]/20'
                :          'bg-zinc-100 text-zinc-400'
              )}
                style={{ fontFamily: HEAD_FONT }}
              >
                {done ? <CheckIcon className="h-4 w-4 stroke-[3]" /> : num}
              </div>
              <span className={clsx(
                'mt-1.5 text-[10px] font-bold uppercase tracking-wide text-center leading-tight whitespace-nowrap',
                active ? 'text-[#0d1f14]' : done ? 'text-[#5a8a1f]' : 'text-zinc-400'
              )}
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >{label}</span>
            </div>
            {i < labels.length - 1 && (
              <div className={clsx(
                'flex-1 h-0.5 mx-1.5 mb-5 rounded-full transition-colors',
                num < current ? 'bg-[#b5f03d]' : 'bg-zinc-200'
              )} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ── Active pricing tier banner ──────────────────────────────
 * Renders for tiered seasons (season.pricingMode === 'tiered'). Shows
 * the currently-active tier name + price; adds urgency cues when the
 * window is closing (within 14 days) or the tier is close to capacity
 * (< 5 spots remaining at this price). The cues are deliberately quiet
 * — we want the parent to feel the deadline, not the form to scream
 * "BUY NOW" at them.
 *
 * Server-side resolution at checkout is the source of truth — this is
 * a display surface and a soft urgency nudge, nothing more.
 */
function TierBanner({ tier, tierFillCounts, lang }) {
  const now = new Date();
  const endsAt = endOfSeasonDay(tier.endsAt); // tier window includes its full end day

  const msUntilEnd = endsAt ? endsAt.getTime() - now.getTime() : null;
  const daysUntilEnd = msUntilEnd != null ? Math.max(0, Math.ceil(msUntilEnd / (1000 * 60 * 60 * 24))) : null;
  const showEndingSoon = daysUntilEnd != null && daysUntilEnd <= 14;
  const endLabel = endsAt ? format(endsAt, lang === 'es' ? "d 'de' MMM" : 'EEEE MMM d', lang === 'es' ? { locale: es } : undefined) : null;

  const cap = tier.maxRegistrations;
  const filled = tierFillCounts?.[tier.id] || 0;
  const spotsLeftAtTier = cap != null ? Math.max(0, cap - filled) : null;
  // Only show the cap urgency when there's a real cap AND <5 spots left —
  // otherwise it reads as filler.
  const showSpotsUrgency = spotsLeftAtTier != null && spotsLeftAtTier < 5 && spotsLeftAtTier > 0;

  return (
    <div
      className="rounded-2xl border-2 border-[#b5f03d] bg-[#b5f03d]/10 px-4 py-3.5 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p
            className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
            style={{ fontFamily: HEAD_FONT }}
          >
            {lang === 'es' ? 'Precio actual' : 'Currently active'}
          </p>
          <p
            className="text-base font-bold uppercase text-[#0d1f14] mt-0.5 truncate"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.4px' }}
          >
            {tier.name}
          </p>
        </div>
        <p
          className="text-2xl font-bold text-[#0d1f14] shrink-0"
          style={{ fontFamily: HEAD_FONT }}
        >
          {formatTierPrice(tier.priceInCents)}
        </p>
      </div>
      {(showEndingSoon || showSpotsUrgency) && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {showEndingSoon && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-0.5 text-[11px] font-bold uppercase text-[#3d6010] border border-[#b5f03d]/40"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              <CalendarDaysIcon className="h-3 w-3" />
              {tier.name} {lang === 'es' ? `termina ${endLabel}` : `ends ${endLabel}`}
            </span>
          )}
          {showSpotsUrgency && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-red-500 px-2.5 py-0.5 text-[11px] font-bold uppercase text-white"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              <UserGroupIcon className="h-3 w-3" />
              {lang === 'es'
                ? `${spotsLeftAtTier} lugar${spotsLeftAtTier !== 1 ? 'es' : ''} a este precio`
                : `${spotsLeftAtTier} spot${spotsLeftAtTier !== 1 ? 's' : ''} left at this price`}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function Card({ title, subtitle, icon, children }) {
  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-zinc-100">
        {icon && <span className="text-lg leading-none">{icon}</span>}
        <div>
          <h2
            className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
            style={{ fontFamily: HEAD_FONT }}
          >
            {title}
          </h2>
          {subtitle && <p className="text-xs text-zinc-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="px-5 py-5 space-y-4">{children}</div>
    </div>
  );
}

function DiscountToggle({ name, checked, onChange, disabled, label, saves }) {
  // Selected state uses lime to match the rest of the system. Savings
  // pill stays a calmer green to read as "money in your pocket" without
  // competing with the primary lime CTA on the same screen.
  return (
    <label className={clsx(
      'flex items-start gap-3 cursor-pointer rounded-xl border-2 p-4 transition-all',
      checked ? 'border-[#b5f03d] bg-[#b5f03d]/10' : 'border-zinc-200 hover:border-[#b5f03d]/40 bg-white'
    )}>
      <input type="checkbox" name={name} checked={checked} onChange={onChange} disabled={disabled}
        className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-[#5a8a1f] focus:ring-[#b5f03d]" />
      <div>
        <p className="text-sm font-semibold text-[#0d1f14]">{label}</p>
        <p className="text-xs text-emerald-700 font-bold mt-0.5">{saves}</p>
      </div>
    </label>
  );
}

function PaymentCard({ selected, onSelect, title, subtitle, badge, amount, amountLabel,
                       schedule, scheduleLabel, hideLabel, disabled }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      onClick={disabled ? undefined : onSelect}
      className={clsx(
        'rounded-xl border-2 p-4 cursor-pointer transition-all',
        selected ? 'border-[#b5f03d] bg-[#b5f03d]/10' : 'border-zinc-200 bg-white hover:border-[#b5f03d]/40'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={clsx(
            'mt-0.5 h-5 w-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors',
            selected ? 'border-[#5a8a1f] bg-[#b5f03d]' : 'border-zinc-300'
          )}>
            {selected && <CheckIcon className="h-3 w-3 text-[#0d1f14] stroke-[3]" />}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className={clsx('text-sm font-bold', selected ? 'text-[#0d1f14]' : 'text-zinc-700')}>{title}</p>
              {badge && (
                <span
                  className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  {badge}
                </span>
              )}
            </div>
            <p className={clsx('text-xs mt-0.5', selected ? 'text-[#5a8a1f]' : 'text-zinc-500')}>{subtitle}</p>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p
            className={clsx('text-xl font-bold leading-none', selected ? 'text-[#0d1f14]' : 'text-zinc-700')}
            style={{ fontFamily: HEAD_FONT }}
          >
            {amount}
          </p>
          <p
            className="text-[10px] uppercase tracking-wide text-zinc-500 mt-0.5 font-semibold"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
          >
            {amountLabel}
          </p>
        </div>
      </div>

      {/* Expandable schedule — only for installment options */}
      {schedule && selected && (
        <div className="mt-3 pt-3 border-t border-[#b5f03d]/30">
          <button type="button"
            onClick={(e) => { e.stopPropagation(); setExpanded(x => !x); }}
            className="text-xs text-[#5a8a1f] hover:text-[#3d6010] font-bold uppercase tracking-wide"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
          >
            {expanded ? `▲ ${hideLabel}` : `▼ ${scheduleLabel}`}
          </button>
          {expanded && (
            <div className="mt-2 space-y-1.5">
              {schedule.map((item, i) => (
                <div key={i} className="flex justify-between text-xs">
                  <span className="text-zinc-500">{item.label}</span>
                  <span className="font-semibold text-[#0d1f14]">{fmtMoney(item.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SuccessScreen({ team, season, playerName, lang }) {
  return (
    <div className="min-h-screen bg-[#f7faf5] flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🎉</div>
          <p
            className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
            style={{ fontFamily: HEAD_FONT }}
          >
            {lang === 'en' ? 'Registration confirmed' : 'Registro confirmado'}
          </p>
          <h1
            className="text-3xl font-bold uppercase tracking-tight text-[#0d1f14] mt-1"
            style={{ fontFamily: HEAD_FONT }}
          >
            {lang === 'en' ? "You're in!" : '¡Estás dentro!'}
          </h1>
          <p className="text-zinc-600 mt-2 text-sm">
            {lang === 'en'
              ? `${playerName} is officially registered for ${season?.name}.`
              : `${playerName} está oficialmente registrado/a para ${season?.name}.`}
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden">
          {/* Team header — pitch-dark mini-band so the team identity
              carries through to the confirmation page (mirrors the page
              hero treatment above). */}
          <div className="flex items-center gap-3 px-6 py-4 border-b border-zinc-100 bg-[#0d1f14]">
            {team?.logoUrl ? (
              <img src={team.logoUrl} alt={team.name} className="h-10 w-10 rounded-xl object-cover ring-2 ring-[#b5f03d]/40 bg-white p-0.5 flex-shrink-0" />
            ) : (
              <div className="h-10 w-10 rounded-xl bg-[#b5f03d] flex items-center justify-center flex-shrink-0">
                <span
                  className="text-sm font-bold text-[#0d1f14] uppercase"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  {team?.name?.[0]}
                </span>
              </div>
            )}
            <div>
              <p className="font-bold text-white text-sm" style={{ fontFamily: HEAD_FONT }}>{team?.name}</p>
              <p className="text-xs text-[#b5f03d]/90">{season?.name}</p>
            </div>
          </div>

          {/* Checklist */}
          <div className="px-6 py-5 space-y-3">
            {[
              lang === 'en' ? 'Registration complete & saved' : 'Registro completo y guardado',
              lang === 'en' ? 'Waiver signed & timestamped'   : 'Exención firmada y registrada',
              lang === 'en' ? 'Confirmation email on its way'  : 'Correo de confirmación en camino',
            ].map((text, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="h-5 w-5 rounded-full bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckIcon className="h-3 w-3 text-[#5a8a1f] stroke-[3]" />
                </div>
                <p className="text-sm text-zinc-700">{text}</p>
              </div>
            ))}

            {/* Documents note */}
            <div className="flex items-start gap-3 pt-1">
              <div className="h-5 w-5 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-[10px]">📄</span>
              </div>
              <p className="text-sm text-zinc-600">
                {lang === 'en'
                  ? 'Your coach may reach out about eligibility documents (birth certificate, photo ID).'
                  : 'Tu entrenador puede comunicarse sobre documentos de elegibilidad (acta de nacimiento, foto).'}
              </p>
            </div>
          </div>

          <div className="px-6 pb-5">
            <p className="text-xs text-zinc-500 text-center">
              {lang === 'en'
                ? `Questions? Contact ${team?.name ?? 'your coach'} directly.`
                : `¿Preguntas? Contacta directamente a ${team?.name ?? 'tu entrenador'}.`}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusScreen({ icon, title, body }) {
  return (
    <div className="min-h-screen bg-[#f7faf5] flex items-center justify-center p-4">
      <div className="max-w-sm w-full bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-8 text-center">
        <div className="text-5xl mb-5">{icon}</div>
        <h1
          className="text-xl font-bold uppercase tracking-tight text-[#0d1f14]"
          style={{ fontFamily: HEAD_FONT }}
        >
          {title}
        </h1>
        <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

function Req() { return <span className="text-red-500 ml-0.5">*</span>; }
